import { Edit2, Eye, KeyRound, Plus, Search, Trash2, UserIcon } from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import AppButton from "../../components/ui/AppButton";
import Pagination from "../../components/ui/dashboard/Pagination";
import Table, { Column } from "../../components/ui/dashboard/Table";
import { MySelect } from "../../components/ui/Select";
import ThreeDotsMenu from "../../components/ui/ThreeDotsMenu";
import { usePermissions } from "../../hooks/usePermissions";
import { useResendCredentials } from "../../hooks/useResendCredentials";
import { buildApiUrl, cn } from "../../lib/utils";
import DeleteUserModal from "../user/ui/DeleteUserModal";
import { Staff, StaffSearchRequest, StaffSearchResponse } from "./@types";
import { useGetAllStaffMutation } from "./staffApiSlice";
import StaffModal from "./StaffModal";

const StaffDashboardTab: React.FC = () => {
  const navigate = useNavigate();
  const { canCreate, canUpdate, canDelete } = usePermissions();
  const { resend: resendCredentials } = useResendCredentials();
  const location = useLocation();

  const [currentTab] = location.pathname.split("/").filter(Boolean);

  // State management
  const [searchTerm, setSearchTerm] = useState("");
  const [dateAddedFilter, setDateAddedFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [isAddStaffModalOpen, setIsAddStaffModalOpen] = useState(false);

  const [selectedStaff, setSelectedStaff] = useState<Staff | null>(null);

  const [isUpdateStaffModalOpen, setIsUpdateStaffModalOpen] = useState(false);
  const [isDeleteStaffModalOpen, setIsDeleteStaffModalOpen] = useState(false);

  // RTK Query mutation
  const [getAllStaff, { data: staffData, isLoading, error: loadError }] =
    useGetAllStaffMutation();

  const fetchStaff = useCallback(async () => {
    const requestBody: StaffSearchRequest = {
      pageNumber: currentPage,
      pageSize: pageSize,
      keyword: searchTerm || undefined,
    };

    try {
      await getAllStaff(requestBody).unwrap();
    } catch (err) {
      console.error("Failed to fetch staff:", err);
    }
  }, [currentPage, pageSize, searchTerm, getAllStaff]);

  useEffect(() => {
    fetchStaff();
  }, [currentPage, pageSize, searchTerm, dateAddedFilter, fetchStaff]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(0);
  };

  const handleAddStaff = (open: boolean) => {
    setIsAddStaffModalOpen(open);
  };

  // Table columns configuration
  const columns: Column<Staff>[] = [
    {
      key: "fullName",
      header: "Staff Name",
      width: "300px",
      render: (value: string, item: Staff) => (
        <div className="flex items-center space-x-3">
          {item.imageUrl ? (
            <img
              src={buildApiUrl(item.imageUrl)}
              alt={`${item.fullName}`}
              className="w-[30px] h-[30px] rounded-full object-cover border border-gray-300 shadow-sm"
            />
          ) : (
            <div className="w-[30px] h-[30px] rounded-full bg-gray-200 flex items-center justify-center border border-gray-300 shadow-sm p-1">
              <UserIcon className="w-full h-full text-hColor" />
            </div>
          )}
          <div className="flex flex-col">
            <span className="text-hColor capitalize">{item.fullName}</span>
            <span className="text-hColor text-sm">{item.email}</span>
          </div>
        </div>
      ),
    },
    {
      key: "userType",
      header: "User type",
      width: "250px",
      render: (value: string) => <span className="text-hColor">{value}</span>,
    },
    {
      key: "phoneNumber",
      header: "Phone number",
      width: "250px",
      render: (value: string) => <span className="text-hColor">{value}</span>,
    },
    {
      key: "address",
      header: "Home address",
      width: "250px",
      render: (value: string) => <span className="text-hColor">{value}</span>,
    },
    {
      key: "isProStaff",
      header: "Staff Type",
      width: "200px",
      render: (value: boolean) => {
        return (
          <span
            className={cn(
              "text-hColor px-2 py-1 text-[12px] font-semibold",
              value
                ? "text-blue-500 border border-blue-500 bg-blue-50 rounded-full"
                : "text-gray-500 border border-gray-500 bg-gray-50 rounded-full"
            )}
          >
            {value ? "Pro Staff" : "Regular Staff"}
          </span>
        );
      },
    },
    {
      key: "actions",
      header: "",
      width: "12",
      align: "center" as const,
      render: (value: any, item: Staff) => {
        return (
          <ThreeDotsMenu
            actions={[
              ...(canUpdate("Users")
                ? [
                    {
                      label: "Edit",
                      onClick: () => {
                        setSelectedStaff(item);
                        setIsUpdateStaffModalOpen(true);
                      },
                      icon: <Edit2 className="w-4 h-4" />,
                    },
                  ]
                : []),
              {
                label: "View staff",
                onClick: () => navigate(`/${currentTab}/staff/${item.id}`),
                icon: <Eye className="w-4 h-4" />,
              },
              ...(canUpdate("Users")
                ? [
                    {
                      label: "Resend credentials",
                      onClick: () => resendCredentials(item.email),
                      icon: <KeyRound className="w-4 h-4" />,
                    },
                  ]
                : []),
              ...(canDelete("Users")
                ? [
                    {
                      label: "Delete staff",
                      onClick: () => {
                        setSelectedStaff(item);
                        setIsDeleteStaffModalOpen(true);
                      },
                      icon: <Trash2 className="w-4 h-4" />,
                    },
                  ]
                : []),
            ]}
          />
        );
      },
    },
  ];

  const handleClose = () => {
    fetchStaff();
    handleAddStaff(false);
    setIsUpdateStaffModalOpen(false);
    setIsDeleteStaffModalOpen(false);
  };

  const response = staffData?.data as StaffSearchResponse | undefined;

  return (
    <div>
      <div className="space-y-6">
        {/* Filters and Actions */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 items-center justify-between bg-white rounded-[10px] p-4">
          <div className="flex flex-col sm:flex-row gap-3">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="text-[16px] pl-9 pr-4 py-2 border border-[#F6F6F6] bg-[#F6F6F6] rounded-[10px] focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary w-full sm:w-[367px] h-[42px]"
              />
            </div>

            {/* Filters */}
            <div className="flex gap-3">
              <div className="relative">
                <MySelect<string>
                  name={"name"}
                  id={"id"}
                  label={""}
                  options={[
                    "Today",
                    "This week",
                    "This month",
                    "This year",
                    "All time",
                  ]}
                  getValue={(option: string) => option}
                  getName={(option: string) => option}
                  value={dateAddedFilter}
                  onChange={(e) => setDateAddedFilter(e)}
                  onValChange={(e) => setDateAddedFilter(e)}
                  placeholder={"Date"}
                  disabled={isLoading}
                  className={cn(
                    "focus:ring-primary focus:border-primary",
                    {
                      "opacity-50": isLoading,
                    },
                    "w-[121px] h-[42px] text-[16px] text-[#535353] appearance-none bg-white border border-[#CECECE] rounded-lg px-4 py-2 pr-8 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                  )}
                />
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          {canCreate("Users") && (
            <div className="flex gap-3">
              <AppButton
                onClick={() => handleAddStaff(true)}
                className="flex items-center px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors min-w-[146px] h-[42px]"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add staff
              </AppButton>
            </div>
          )}
        </div>

        {/* Table */}
        <div className="overflow-hidden space-y-6">
          <Table
            data={response?.data || []}
            columns={columns}
            loading={isLoading}
            error={loadError}
            emptyMessage="No staff found"
          />

          {/* Pagination */}
          {response && (
            <Pagination
              currentPage={response.currentPage}
              totalPages={response.totalPages}
              pageSize={response.pageSize}
              totalCount={response.totalCount}
              hasNextPage={response.hasNextPage}
              hasPreviousPage={response.hasPreviousPage}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
            />
          )}
        </div>
      </div>
      {isAddStaffModalOpen && (
        <StaffModal isOpen={isAddStaffModalOpen} onClose={handleClose} />
      )}

      {isUpdateStaffModalOpen && selectedStaff && (
        <StaffModal
          isOpen={isUpdateStaffModalOpen}
          onClose={handleClose}
          staff={selectedStaff}
          mode="edit"
        />
      )}

      <DeleteUserModal
        isOpen={isDeleteStaffModalOpen}
        onClose={handleClose}
        userId={selectedStaff?.id}
        entityLabel="staff"
      />
    </div>
  );
};

export default StaffDashboardTab;
